import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { connect } from "../lib/ws";
import { Badge, Button, FieldGroup, Input, Label, Panel, Spinner } from "../components/ui";

export function LobbyBrowserScreen() {
  const navigate = useNavigate();
  const username = useAuthStore((s) => s.username) ?? "Giocatore";
  const [lobbyName, setLobbyName] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const lobbies = useQuery({ queryKey: ["lobbies"], queryFn: () => api.listLobbies(), refetchInterval: 10_000 });

  const create = useMutation({
    mutationFn: () => api.createLobby(username, lobbyName || undefined, isPrivate),
    onSuccess: (r) => {
      connect();
      navigate(`/lobby/${r.code}`);
    },
    onError: () => setErr("Creazione fallita"),
  });

  const joinByCode = useMutation({
    mutationFn: async (code: string) => api.validateLobby(code.toUpperCase()),
    onSuccess: (r) => {
      if (!r.exists) { setErr("Lobby non trovata"); return; }
      connect();
      navigate(`/lobby/${r.lobby_id}`);
    },
    onError: () => setErr("Lobby non trovata"),
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-ink">Salone</h1>
        <p className="text-sm text-ink-dim">Crea una partita o entra con un codice condiviso dal tavolo.</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Panel>
          <h2 className="label-caps mb-4">Crea partita</h2>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          >
            <FieldGroup>
              <Label>Nome lobby (opzionale)</Label>
              <Input value={lobbyName} maxLength={30} onChange={(e) => setLobbyName(e.target.value)} placeholder="La mia partita…" />
            </FieldGroup>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink-dim">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} className="h-4 w-4 accent-brass" />
              🔒 Privata (solo con codice)
            </label>
            <Button type="submit" disabled={create.isPending}>{create.isPending ? "…" : "Crea partita"}</Button>
            {err && <p className="text-sm text-crimson">{err}</p>}
          </form>
        </Panel>

        <Panel>
          <h2 className="label-caps mb-4">Entra con codice</h2>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => { e.preventDefault(); if (joinCode.trim()) joinByCode.mutate(joinCode.trim()); }}
          >
            <FieldGroup>
              <Label>Codice lobby</Label>
              <Input
                value={joinCode}
                maxLength={6}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                className="text-center font-mono text-xl tracking-[0.2em]"
                style={{ letterSpacing: "0.2em" }}
              />
            </FieldGroup>
            <Button type="submit" disabled={joinByCode.isPending || joinCode.length < 4}>{joinByCode.isPending ? "…" : "Entra"}</Button>
          </form>
        </Panel>
      </div>

      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="label-caps">Lobby pubbliche</h2>
          <button className="btn-ghost px-3 py-1 text-xs" onClick={() => lobbies.refetch()}>Aggiorna</button>
        </div>
        <Panel className="p-0 overflow-hidden">
          {lobbies.isLoading && <div className="flex items-center gap-2 p-6 text-ink-dim"><Spinner /> Caricamento…</div>}
          {lobbies.data?.length === 0 && <div className="p-6 text-center text-sm text-ink-faint">Nessuna lobby pubblica. Creane una!</div>}
          <ul className="divide-y divide-line/60">
            {lobbies.data?.map((l) => (
              <li key={l.lobby_id} className="flex items-center justify-between p-4 hover:bg-surface-2/50">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-base text-ink">{l.name}</span>
                    {l.is_private && <Badge tone="dim">privata</Badge>}
                  </div>
                  <p className="text-xs text-ink-dim">di {l.creator_name} · {l.player_count}/{l.max_players}</p>
                </div>
                <Button
                  variant="ghost"
                  disabled={l.player_count >= l.max_players}
                  onClick={() => { connect(); navigate(`/lobby/${l.code}`); }}
                  className="px-4 py-1.5 text-xs"
                >
                  Entra
                </Button>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
